import { z } from 'zod';

// ─── AI availability ─────────────────────────────────────────────────────────
//
// Whether Sona (the AI) may be offered to a member at all, derived by the
// server on every read and never stored, so a change of address or program
// re-classifies the member on the next read.
//
// - `available`: nothing beyond the ordinary consent gate stands between the
//   member and Sona. A member with no consent decision on record is available.
// - `opted_out`: the member's latest Sona consent decision is a decline. The
//   choice is theirs and reversible: practices stay visible and anything that
//   needs Sona asks for consent first.
// - `restricted`: Sona is hard-disabled for the member and they cannot turn it
//   back on. `reason` says why: `state` (the member's current address is in a
//   jurisdiction where AI care is restricted) or `oud` (an active enrollment in
//   an opioid use disorder program). Clients branch on the status; the reason is
//   for routing analytics and copy selection, never for re-deriving the rule.
//
// Declared once here for the API and every client (ADR-066).

export const AI_AVAILABILITY_STATUS = ['available', 'opted_out', 'restricted'] as const;
export const aiAvailabilityStatusSchema = z.enum(AI_AVAILABILITY_STATUS);
export type AiAvailabilityStatus = z.infer<typeof aiAvailabilityStatusSchema>;

export const AI_RESTRICTED_REASON = ['state', 'oud'] as const;
export const aiRestrictedReasonSchema = z.enum(AI_RESTRICTED_REASON);
export type AiRestrictedReason = z.infer<typeof aiRestrictedReasonSchema>;

/**
 * Only `restricted` carries a reason; the union makes a reason on any other
 * status unrepresentable.
 *
 * `state` is the two-letter USPS code of the member's current home state, the
 * value the restriction matched. The server sends it only when `reason` is
 * `state`, so a client can name the state to the member. It is the member's
 * own address data: never put it in a log, metric tag, analytics property or
 * route param. Optional so a client reads a server that predates it.
 */
export const aiAvailabilitySchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('available') }),
  z.object({ status: z.literal('opted_out') }),
  z.object({
    status: z.literal('restricted'),
    reason: aiRestrictedReasonSchema,
    state: z.string().min(1).optional(),
  }),
]);
export type AiAvailability = z.infer<typeof aiAvailabilitySchema>;
